/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CANCEL_DELETION_STATUS } from '../../../constants/accountRecovery';
import {
  describeSsoError,
  type ResolvedSsoError,
} from '../../../utils/ssoErrors';
import type { OnOtpVerified, OtpFlow } from '../types';
import { useVerifyEmailLogin } from '../../useVerifyEmailLogin';
import { useRequestPasswordlessCode } from '../../useRequestPasswordlessCode';

export interface UsePasswordlessOtpFlowOptions {
  email?: string;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
  isSignup?: boolean;
  /** Set only after the user confirmed keep-account on the login screen. */
  cancelDeletion?: boolean;
}

export function usePasswordlessOtpFlow({
  email,
  startTimer,
  onVerified,
  isSignup = false,
  cancelDeletion = false,
}: UsePasswordlessOtpFlowOptions): OtpFlow {
  const { t } = useTranslation('account');
  // Keep-account restores an existing login; it never applies to signup.
  const sendCancelDeletion = cancelDeletion && !isSignup;

  const verifyCodeMutation = useVerifyEmailLogin();
  const requestCodeMutation = useRequestPasswordlessCode();

  const [localError, setLocalError] = useState<ResolvedSsoError>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setRequestError = useCallback(
    (error: unknown, fallback: string) =>
      setLocalError(describeSsoError(error, t, fallback)),
    [t],
  );

  const submitOtp = useCallback(
    (otp: string) => {
      if (!email) {
        setLocalError({ message: t('verifyEmailPage.missingEmail') });
        return;
      }

      void (async () => {
        setIsSubmitting(true);
        try {
          // Verifying persists the session; the screen's sign-in tail navigates.
          const session = await verifyCodeMutation.mutateAsync({
            username: email,
            otp,
            ...(sendCancelDeletion ? { status: CANCEL_DELETION_STATUS } : {}),
          });

          if (isSignup) {
            await onVerified?.({ signupActivation: true });
          }

          // Left true on success: onVerified navigates and this screen unmounts.
          await onVerified?.({ session, userId: session?.userId ?? null });
        } catch (error) {
          setRequestError(error, t('verifyEmailPage.verificationFailed'));
          setIsSubmitting(false);
        }
      })();
    },
    [
      email,
      sendCancelDeletion,
      isSignup,
      onVerified,
      t,
      verifyCodeMutation,
      setRequestError,
    ],
  );

  const requestOtp = useCallback(() => {
    if (!email) {
      setLocalError({ message: t('verifyEmailPage.missingEmail') });
      return;
    }

    setLocalError(undefined);

    requestCodeMutation.mutate(
      {
        username: email,
        ...(sendCancelDeletion ? { status: CANCEL_DELETION_STATUS } : {}),
      },
      {
        onSuccess: () => {
          startTimer();
        },
        onError: (error: Error) => {
          setRequestError(error, t('verifyEmailPage.requestFailed'));
          startTimer();
        },
      },
    );
  }, [
    email,
    sendCancelDeletion,
    requestCodeMutation,
    setRequestError,
    startTimer,
    t,
  ]);

  const clearError = useCallback(() => {
    setLocalError(undefined);
    verifyCodeMutation.reset();
    requestCodeMutation.reset();
  }, [verifyCodeMutation, requestCodeMutation]);

  return {
    submitOtp,
    requestOtp,
    errorMessage: localError?.message,
    errorCode: localError?.code,
    isLoading: isSubmitting || requestCodeMutation.isPending,
    clearError,
  };
}
